import { Box, SxProps, Theme } from "@mui/material";
import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormControlNumberInput } from "@/components/formControls/formControlNumberInput";
import { FormControlToggleButtonGroup, ToggleOption } from "@/components/formControls/formControlToggleButtonGroup";
import { useGetErrorMessage } from "@/hooks/useGetErrorMessage";
import {
  BasementConversionEnum,
  BasementEnum,
  BuildingTypeEnum,
  Construction,
  HouseOrApartmentTypeEnum,
  ModernizationMethodEnum,
  ObjectTypeEnum,
  OutdoorFacilitiesEnum,
  RoofEnum,
} from "@/types/refineRealEstate";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";
import { REAL_ESTATE_VALIDATION_LIMITS, getUnitsNumberLimits } from "@/validation/realEstateValidationLimits";

import { StyledFormGridContainer, StyledFormSection, StyledFormSectionTitle } from "../refineRealEstate.styled";
import { defaultModernizationsMatrix } from "./defaultModernizationEquipmentMatrix";

const toggleButtonGroupStyle: SxProps<Theme> = {
  gap: "4px",
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
};

interface BuildingDataTabProps {
  readonly?: boolean;
}

export const BuildingDataTab = ({ readonly }: BuildingDataTabProps): JSX.Element => {
  const { t } = useTranslation("Common", { keyPrefix: "realEstate" });
  const getErrorMessage = useGetErrorMessage();

  const {
    control,
    formState: { errors },
    trigger,
    watch,
    setValue,
  } = useFormContext<PropertyInformation>();

  const { generalInfo, buildingData } = watch();

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    if (buildingData.constructionYear && currentYear - buildingData.constructionYear < 10) {
      setValue("modernization", {
        method: ModernizationMethodEnum.detailed,
        matrix: defaultModernizationsMatrix,
        refurbishmentYear: undefined,
        completionStatus: "completedMinimal",
      });
    } else {
      trigger("modernization.refurbishmentYear");
    }
    trigger();
  }, [buildingData.constructionYear, setValue, trigger]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  const isNoBasement = buildingData.basement === BasementEnum.none;

  useEffect(() => {
    if (isNoBasement) {
      setValue("buildingData.basementConversion", BasementConversionEnum.none);
    }
  }, [isNoBasement, setValue]);

  const houseOrApartmentTypeOptions: ToggleOption<HouseOrApartmentTypeEnum>[] = useMemo(
    () => [
      {
        value: HouseOrApartmentTypeEnum.detachedHouse,
        label: t("houseOrApartmentType.values.detachedHouse"),
        disabled: !!readonly,
      },
      {
        value: HouseOrApartmentTypeEnum.semiDetachedHouse,
        label: t("houseOrApartmentType.values.semidetachedHouse"),
        disabled: !!readonly,
      },
      {
        value: HouseOrApartmentTypeEnum.endTerraceHouse,
        label: t("houseOrApartmentType.values.endTerraceHouse"),
        disabled: !!readonly,
      },
      {
        value: HouseOrApartmentTypeEnum.midTerracedHouse,
        label: t("houseOrApartmentType.values.midTerracedHouse"),
        disabled: !!readonly,
      },
    ],
    [readonly, t],
  );

  const constructionOptions: ToggleOption<Construction>[] = useMemo(
    () => [
      { value: Construction.massive, label: t("construction.values.solid"), disabled: !!readonly },
      { value: Construction.prefabHouseSolid, label: t("construction.values.prefabHouseSolid"), disabled: !!readonly },
      { value: Construction.prefabHouseWood, label: t("construction.values.prefabHouseWood"), disabled: !!readonly },
    ],
    [readonly, t],
  );

  const roofOptions: ToggleOption<RoofEnum>[] = useMemo(
    () => [
      { value: RoofEnum.full, label: t("roof.values.fullyExpanded"), disabled: !!readonly },
      { value: RoofEnum.partial, label: t("roof.values.partiallyExpanded"), disabled: !!readonly },
      { value: RoofEnum.none, label: t("roof.values.notExpanded"), disabled: !!readonly },
      { value: RoofEnum.flatRoof, label: t("roof.values.flatRoof"), disabled: !!readonly },
    ],
    [readonly, t],
  );

  const basementOptions: ToggleOption<BasementEnum>[] = useMemo(
    () => [
      { value: BasementEnum.full, label: t("basement.values.fullBasement"), disabled: !!readonly },
      { value: BasementEnum.partial, label: t("basement.values.partialBasement"), disabled: !!readonly },
      { value: BasementEnum.none, label: t("basement.values.noBasement"), disabled: !!readonly },
    ],
    [readonly, t],
  );

  const basementConversionOptions: ToggleOption<BasementConversionEnum>[] = [
    {
      value: BasementConversionEnum.full,
      label: t("basementConversion.values.full"),
      disabled: isNoBasement || !!readonly,
    },
    {
      value: BasementConversionEnum.partial,
      label: t("basementConversion.values.partial"),
      disabled: isNoBasement || !!readonly,
    },
    {
      value: BasementConversionEnum.none,
      label: t("basementConversion.values.none"),
      disabled: isNoBasement || !!readonly,
    },
  ];

  const yesNoOptions: ToggleOption<boolean>[] = [
    { value: true, label: t("yes"), disabled: !!readonly },
    { value: false, label: t("no"), disabled: !!readonly },
  ];

  const evaluationOfTheOutdoorFacilitiesOptions: ToggleOption<OutdoorFacilitiesEnum>[] = useMemo(
    () => [
      {
        value: OutdoorFacilitiesEnum.none,
        label: t("evaluationOfTheOutdoorFacilities.values.no"),
        disabled: !!readonly,
      },
      {
        value: OutdoorFacilitiesEnum.belowAverage,
        label: t("evaluationOfTheOutdoorFacilities.values.belowAverage"),
        disabled: !!readonly,
      },
      {
        value: OutdoorFacilitiesEnum.average,
        label: t("evaluationOfTheOutdoorFacilities.values.average"),
        disabled: !!readonly,
      },
      {
        value: OutdoorFacilitiesEnum.aboveAverage,
        label: t("evaluationOfTheOutdoorFacilities.values.aboveAverage"),
        disabled: !!readonly,
      },
    ],
    [readonly, t],
  );

  const isHouse = generalInfo.objectType === ObjectTypeEnum.house;
  const isApartment = generalInfo.buildingType === BuildingTypeEnum.apartment;
  const isMultiFamilyHouse = generalInfo.buildingType === BuildingTypeEnum.multiFamilyHouse;
  const isMixedUseProperty = generalInfo.buildingType === BuildingTypeEnum.mixedUseProperty;
  const unitsNumberLimits = getUnitsNumberLimits(
    isMultiFamilyHouse || isMixedUseProperty ? ObjectTypeEnum.multiFamilyHouse : ObjectTypeEnum.apartment,
  );

  return (
    <StyledFormGridContainer>
      {isMixedUseProperty && (
        <>
          <StyledFormSection>
            <StyledFormSectionTitle>{t("constructionYear")}</StyledFormSectionTitle>
            <FormControlNumberInput
              name="buildingData.constructionYear"
              control={control}
              fullWidth
              error={!!errors.buildingData?.constructionYear}
              helperText={getErrorMessage(errors.buildingData?.constructionYear)}
              data-cy="refine-data-construction-year"
              disabled={!!readonly}
            />
          </StyledFormSection>
          <Box />
        </>
      )}

      {!isMixedUseProperty && (
        <StyledFormSection>
          <StyledFormSectionTitle>{t("constructionYear")}</StyledFormSectionTitle>
          <FormControlNumberInput
            name="buildingData.constructionYear"
            control={control}
            fullWidth
            error={!!errors.buildingData?.constructionYear}
            helperText={getErrorMessage(errors.buildingData?.constructionYear)}
            data-cy="refine-data-construction-year"
            disabled={!!readonly}
          />
        </StyledFormSection>
      )}

      <StyledFormSection>
        <StyledFormSectionTitle>{`${t("livingArea")} (m²)`}</StyledFormSectionTitle>
        <FormControlNumberInput
          name="buildingData.livingArea"
          control={control}
          fullWidth
          error={!!errors.buildingData?.livingArea}
          helperText={getErrorMessage(errors.buildingData?.livingArea)}
          data-cy="refine-data-living-area"
          disabled={!!readonly}
        />
      </StyledFormSection>

      {isMixedUseProperty && (
        <>
          <StyledFormSection>
            <StyledFormSectionTitle>{t("unitsNumber")}</StyledFormSectionTitle>
            <FormControlNumberInput
              name="buildingData.unitsNumber"
              control={control}
              fullWidth
              error={!!errors.buildingData?.unitsNumber}
              helperText={getErrorMessage(errors.buildingData?.unitsNumber)}
              sx={{ flexGrow: 1, pr: "5px" }}
              numericFormatSx={{ "& .MuiOutlinedInput-root": { height: "45px" } }}
              buttonsEnabled={!readonly}
              minValue={1}
              disabled={!!readonly}
              data-cy="refine-data-units-number"
            />
          </StyledFormSection>
          <StyledFormSection>
            <StyledFormSectionTitle>{t("commercialArea")} (m²)</StyledFormSectionTitle>
            <FormControlNumberInput
              name="buildingData.commercialArea"
              control={control}
              fullWidth
              error={!!errors.buildingData?.commercialArea}
              helperText={getErrorMessage(errors.buildingData?.commercialArea)}
              minValue={1}
              data-cy="refine-data-commercial-area"
              disabled={!!readonly}
            />
          </StyledFormSection>
          <StyledFormSection>
            <StyledFormSectionTitle>{t("commercialUnits")}</StyledFormSectionTitle>
            <FormControlNumberInput
              name="buildingData.commercialUnits"
              control={control}
              fullWidth
              minValue={1}
              maxValue={998}
              error={!!errors.buildingData?.commercialUnits}
              helperText={getErrorMessage(errors.buildingData?.commercialUnits)}
              data-cy="refine-data-commercial-units"
              disabled={!!readonly}
            />
          </StyledFormSection>
        </>
      )}

      {isHouse && (
        <Box sx={{ gridColumn: "span 2" }}>
          <FormControlToggleButtonGroup
            label={t("houseOrApartmentType.title")}
            control={control}
            name="buildingData.houseOrApartmentType"
            fullWidth
            sx={toggleButtonGroupStyle}
            options={houseOrApartmentTypeOptions}
            data-cy="refine-data-house-or-apartment-type"
          />
        </Box>
      )}
      {(isHouse || isMultiFamilyHouse || isMixedUseProperty) && (
        <Box sx={{ gridColumn: "span 2" }}>
          <FormControlToggleButtonGroup
            label={t("construction.title")}
            control={control}
            name="buildingData.construction"
            errorMsg={getErrorMessage(errors.buildingData?.construction)}
            fullWidth
            sx={toggleButtonGroupStyle}
            options={constructionOptions}
            data-cy="refine-data-construction"
          />
        </Box>
      )}

      {(isHouse || isMultiFamilyHouse || isMixedUseProperty) && (
        <StyledFormSection>
          <StyledFormSectionTitle>{t("numberOfFloors")}</StyledFormSectionTitle>
          <FormControlNumberInput
            name="buildingData.numberOfFloors"
            control={control}
            fullWidth
            sx={{ flexGrow: 1, pr: "5px" }}
            numericFormatSx={{ "& .MuiOutlinedInput-root": { height: "45px" } }}
            buttonsEnabled={!readonly}
            minValue={REAL_ESTATE_VALIDATION_LIMITS.numberOfFloors.min}
            error={!!errors.buildingData?.numberOfFloors}
            helperText={getErrorMessage(errors.buildingData?.numberOfFloors)}
            disabled={!!readonly}
            data-cy="refine-data-number-of-floors"
          />
        </StyledFormSection>
      )}
      {(isHouse || isApartment) && (
        <StyledFormSection>
          <StyledFormSectionTitle>{t("roomsNumber")}</StyledFormSectionTitle>
          <FormControlNumberInput
            name="buildingData.rooms"
            control={control}
            fullWidth
            error={!!errors.buildingData?.rooms}
            helperText={getErrorMessage(errors.buildingData?.rooms)}
            numericFormatSx={{ "& .MuiOutlinedInput-root": { height: "45px" } }}
            disabled={!!readonly}
            data-cy="refine-data-rooms"
          />
        </StyledFormSection>
      )}

      {(isApartment || isMultiFamilyHouse) && !isMixedUseProperty && (
        <>
          <StyledFormSection>
            <StyledFormSectionTitle>{t("unitsNumber")}</StyledFormSectionTitle>
            <FormControlNumberInput
              name="buildingData.unitsNumber"
              control={control}
              fullWidth
              error={!!errors.buildingData?.unitsNumber}
              helperText={getErrorMessage(errors.buildingData?.unitsNumber)}
              sx={{ flexGrow: 1, pr: "5px" }}
              numericFormatSx={{ "& .MuiOutlinedInput-root": { height: "45px" } }}
              buttonsEnabled={!readonly}
              minValue={unitsNumberLimits?.min}
              maxValue={unitsNumberLimits?.max}
              disabled={!!readonly}
              data-cy="refine-data-units-number"
            />
          </StyledFormSection>
        </>
      )}

      {isHouse && (
        <Box sx={{ gridColumn: "span 2" }}>
          <FormControlToggleButtonGroup
            label={t("roof.title")}
            control={control}
            name="buildingData.roof"
            fullWidth
            sx={toggleButtonGroupStyle}
            options={roofOptions}
            data-cy="refine-data-roof"
          />
        </Box>
      )}

      {(isHouse || isMultiFamilyHouse || isMixedUseProperty) && (
        <Box sx={{ gridColumn: "span 2" }}>
          <FormControlToggleButtonGroup
            label={t("basement.title")}
            control={control}
            name="buildingData.basement"
            fullWidth
            sx={toggleButtonGroupStyle}
            options={basementOptions}
            data-cy="refine-data-basement"
          />
        </Box>
      )}

      {isHouse && (
        <Box sx={{ gridColumn: "span 2", opacity: isNoBasement ? 0.5 : 1 }}>
          <FormControlToggleButtonGroup
            label={t("basementConversion.title")}
            control={control}
            name="buildingData.basementConversion"
            fullWidth
            sx={toggleButtonGroupStyle}
            options={basementConversionOptions}
            data-cy="refine-data-basement-conversion"
          />
        </Box>
      )}

      {isHouse && (
        <Box sx={{ gridColumn: "span 2" }}>
          <FormControlToggleButtonGroup
            label={t("evaluationOfTheOutdoorFacilities.title")}
            control={control}
            name="buildingData.outdoorFacilities"
            fullWidth
            sx={toggleButtonGroupStyle}
            options={evaluationOfTheOutdoorFacilitiesOptions}
            data-cy="refine-data-outdoor-facilities"
          />
        </Box>
      )}

      {isApartment && (
        <>
          <StyledFormSection>
            <StyledFormSectionTitle>{t("floorNumber.title")}</StyledFormSectionTitle>
            <FormControlNumberInput
              name="buildingData.floorNumber"
              control={control}
              fullWidth
              error={!!errors.buildingData?.floorNumber}
              helperText={getErrorMessage(errors.buildingData?.floorNumber)}
              disabled={!!readonly}
              data-cy="refine-data-floor-number"
            />
          </StyledFormSection>
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("gardenUse")}
              control={control}
              name="buildingData.garden"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={yesNoOptions}
              data-cy="refine-data-garden"
            />
          </Box>
        </>
      )}

      {(isApartment || isMultiFamilyHouse || isMixedUseProperty) && (
        <Box sx={{ gridColumn: "span 2" }}>
          <FormControlToggleButtonGroup
            label={t("elevator")}
            control={control}
            name="buildingData.elevator"
            fullWidth
            sx={toggleButtonGroupStyle}
            options={yesNoOptions}
            data-cy="refine-data-elevator"
          />
        </Box>
      )}

      <StyledFormSection>
        <StyledFormSectionTitle>{t("garages")}</StyledFormSectionTitle>
        <Box sx={{ display: "flex", gap: "5px", gridColumn: "span 2" }}>
          <FormControlNumberInput
            name="buildingData.garages"
            control={control}
            fullWidth
            sx={{ flexGrow: 1, pr: "5px" }}
            numericFormatSx={{ "& .MuiOutlinedInput-root": { height: "45px" } }}
            buttonsEnabled={!readonly}
            minValue={REAL_ESTATE_VALIDATION_LIMITS.garages.range.min}
            maxValue={REAL_ESTATE_VALIDATION_LIMITS.garages.range.max}
            error={!!errors.buildingData?.garages}
            helperText={getErrorMessage(errors.buildingData?.garages)}
            disabled={!!readonly}
            data-cy="refine-data-garages"
          />
        </Box>
      </StyledFormSection>
      {isApartment && (
        <StyledFormSection>
          <StyledFormSectionTitle>{t("outdoorParkingSpaces")}</StyledFormSectionTitle>
          <Box sx={{ display: "flex", gap: "5px", gridColumn: "span 2" }}>
            <FormControlNumberInput
              name="buildingData.outdoorParkingSpaces"
              control={control}
              fullWidth
              sx={{ flexGrow: 1, pr: "5px" }}
              numericFormatSx={{ "& .MuiOutlinedInput-root": { height: "45px" } }}
              buttonsEnabled={!readonly}
              minValue={REAL_ESTATE_VALIDATION_LIMITS.outdoorParkingSpaces.min}
              maxValue={REAL_ESTATE_VALIDATION_LIMITS.outdoorParkingSpaces.max}
              error={!!errors.buildingData?.outdoorParkingSpaces}
              helperText={getErrorMessage(errors.buildingData?.outdoorParkingSpaces)}
              disabled={!!readonly}
              data-cy="refine-data-outdoor-parking-spaces"
            />
          </Box>
        </StyledFormSection>
      )}
    </StyledFormGridContainer>
  );
};
